import type {
  BehaviorState,
  NormalizedStateMachineConfig,
  StateMachineSnapshot,
  StateTransitionRecord,
} from '../../contracts/stateMachine';
import type { BehaviorSignalType } from '../../contracts/signals';

/** Tabela explícita de transições válidas (nenhum `if` espalhado). */
const TRANSITIONS: Readonly<Record<BehaviorState, readonly BehaviorState[]>> = {
  NOT_INITIALIZED: ['INITIALIZING', 'DESTROYING'],
  INITIALIZING: ['ACTIVE', 'DESTROYING'],
  ACTIVE: ['IDLE', 'HIDDEN', 'PAUSED', 'DESTROYING'],
  IDLE: ['ACTIVE', 'HIDDEN', 'PAUSED', 'DESTROYING'],
  HIDDEN: ['ACTIVE', 'IDLE', 'PAUSED', 'DESTROYING'],
  PAUSED: ['ACTIVE', 'IDLE', 'HIDDEN', 'DESTROYING'],
  DESTROYING: ['DESTROYED'],
  DESTROYED: ['INITIALIZING'],
};

/**
 * State machine do runtime Behavior (Etapa 20). Estado por instância, transições
 * explícitas, histórico limitado. Transições inválidas não derrubam o runtime:
 * são contabilizadas. `reinit` cria uma nova geração.
 */
export class BehaviorStateMachine {
  private readonly config: NormalizedStateMachineConfig;

  private state: BehaviorState = 'NOT_INITIALIZED';

  private previousState: BehaviorState | undefined;

  private generation = 0;

  private changedAt: number | undefined;

  private transitionCount = 0;

  private invalidTransitionCount = 0;

  private history: StateTransitionRecord[] = [];

  /** Estado anterior a HIDDEN, para restaurar em VISIBLE (ACTIVE/IDLE). */
  private preHiddenState: BehaviorState = 'ACTIVE';

  constructor(config: NormalizedStateMachineConfig) {
    this.config = config;
  }

  getState(): BehaviorState {
    return this.state;
  }

  getGeneration(): number {
    return this.generation;
  }

  getHistory(): readonly StateTransitionRecord[] {
    return this.history;
  }

  /** Início do ciclo de vida: NOT_INITIALIZED/DESTROYED → INITIALIZING → ACTIVE. */
  beginInit(now: number): void {
    this.generation += 1;
    this.transition('INITIALIZING', 'init', now);
    this.transition('ACTIVE', 'init_complete', now);
  }

  beginDestroy(now: number): void {
    this.transition('DESTROYING', 'destroy', now);
  }

  finishDestroy(now: number): void {
    this.transition('DESTROYED', 'destroy_complete', now);
  }

  /** Pausa/retoma explícitas (não dirigidas por sinais no escopo atual). */
  pause(now: number): boolean {
    return this.transition('PAUSED', 'pause', now);
  }

  resume(now: number): boolean {
    const target = this.preHiddenState === 'IDLE' ? 'IDLE' : 'ACTIVE';
    return this.transition(target, 'resume', now);
  }

  /** Mapeia um sinal para uma transição de estado, se aplicável. */
  applySignal(type: BehaviorSignalType, now: number): void {
    switch (type) {
      case 'ACTIVITY':
        this.transition('ACTIVE', 'signal:ACTIVITY', now);
        break;
      case 'IDLE':
        this.transition('IDLE', 'signal:IDLE', now);
        break;
      case 'HIDDEN':
        if (this.state === 'ACTIVE' || this.state === 'IDLE') {
          this.preHiddenState = this.state;
        }
        this.transition('HIDDEN', 'signal:HIDDEN', now);
        break;
      case 'VISIBLE': {
        if (this.state !== 'HIDDEN') break;
        const target = this.preHiddenState === 'IDLE' ? 'IDLE' : 'ACTIVE';
        this.transition(target, 'signal:VISIBLE', now);
        break;
      }
      default:
        break;
    }
  }

  /** Transição explícita. Retorna false (e contabiliza) se inválida. */
  transition(to: BehaviorState, reason: string, now: number): boolean {
    if (this.state === to) {
      // Reentrância no mesmo estado é no-op (não conta como transição).
      return true;
    }

    const allowed = TRANSITIONS[this.state] ?? [];
    if (!allowed.includes(to)) {
      this.invalidTransitionCount += 1;
      return false;
    }

    const record: StateTransitionRecord = {
      from: this.state,
      to,
      at: now,
      reason,
      generation: this.generation,
    };
    this.previousState = this.state;
    this.state = to;
    this.changedAt = now;
    this.transitionCount += 1;
    this.pushHistory(record);
    return true;
  }

  private pushHistory(record: StateTransitionRecord): void {
    this.history.push(record);
    if (this.history.length > this.config.historyLimit) {
      this.history.splice(0, this.history.length - this.config.historyLimit);
    }
  }

  getSnapshot(): StateMachineSnapshot {
    return {
      enabled: this.config.enabled,
      state: this.state,
      previousState: this.previousState,
      generation: this.generation,
      changedAt: this.changedAt,
      transitionCount: this.transitionCount,
      invalidTransitionCount: this.invalidTransitionCount,
      historySize: this.history.length,
    };
  }
}
